// @section 1 Campaigns / CampaignDetail — drill-through from Overview
// One campaign deep: funnel KPIs from latest snapshot, reply-class breakdown,
// timeseries (sent / replies_engaged / bookings) over the last N days.
// Reads /api/hq/campaigns/{id}/detail — returns meta + latest (31 cols)
// + timeseries (one row per refresh_run, ~daily). 30-day default window.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TrendChart, { type TrendMetric, type TrendRow } from '../../components/TrendChart';
import {
  getCampaignDetail,
  getCampaignDaily,
  CampaignsUnconfigured,
  type CampaignDetail as CampaignDetailType,
  type CampaignDailyActivity,
} from '../../api';

// @section 2 Chart metrics (true per-day activity for this wave/send)
const BASE_FIELDS = ['sent', 'delivered', 'opens', 'demo', 'diag', 'booked', 'replies', 'engaged'];
const METRICS: TrendMetric[] = [
  { key: 'sent', label: 'Sent', kind: 'count', field: 'sent', color: '#14b8a6' },
  { key: 'opens', label: 'Opens', kind: 'count', field: 'opens', color: '#10b981' },
  { key: 'demo', label: 'Demo clicks', kind: 'count', field: 'demo', color: '#f59e0b' },
  { key: 'booked', label: 'Booked', kind: 'count', field: 'booked', color: '#f0606b' },
  { key: 'openRate', label: 'Open rate', kind: 'rate', num: 'opens', den: 'delivered', color: '#10b981' },
];

// @section 3 Main
export default function CampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<CampaignDetailType | null>(null);
  const [daily, setDaily] = useState<CampaignDailyActivity[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);

  // @subsection 3.1 Fetch on mount + on id change — detail (KPIs) + true daily
  useEffect(() => {
    if (!campaignId) return;
    setData(null); setDaily(null); setErr(null); setUnconfigured(false);
    const onErr = (e: unknown) => {
      if (e instanceof CampaignsUnconfigured) setUnconfigured(true); else setErr(String(e));
    };
    getCampaignDetail(campaignId, 120).then(setData).catch(onErr);
    getCampaignDaily(campaignId, 120).then(setDaily).catch(onErr);
  }, [campaignId]);

  // @subsection 3.2 Chart rows — true per-day activity (add derived `delivered`)
  const rows = useMemo<TrendRow[]>(() => {
    if (!daily) return [];
    return daily.map((d) => ({
      date: d.activity_date.slice(0, 10),
      sent: d.sent, bounced: d.bounced, delivered: Math.max(d.sent - d.bounced, 0),
      opens: d.opens, demo: d.demo_clicks, diag: d.diag_visits,
      engaged: d.replies_engaged, booked: d.booked, replies: d.replies,
    }));
  }, [daily]);

  const latest = data?.latest ?? null;
  const meta = data?.meta ?? null;

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto">

          {/* @subsection 3.3 Breadcrumb */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <button onClick={() => navigate('/campaigns')}
                      className="text-accent-400 hover:underline">Campaigns</button>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">{meta?.campaign_name ?? campaignId?.slice(0, 8) + '…'}</span>
            </div>
            <button onClick={() => navigate('/campaigns')}
                    className="text-xs text-accent-400 hover:underline">← back to all campaigns</button>
          </div>

          {/* @subsection 3.4 Title + funnel KPIs */}
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6 mb-8">
            <div className="flex-shrink-0">
              <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">
                {meta?.vertical_id ?? 'Campaign'}
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100 mb-2">
                {meta?.campaign_name ?? '—'}
              </h1>
              <p className="text-sm text-muted md:whitespace-nowrap">
                {meta ? `last snapshot ${shortTs(meta.last_taken_at)}` : 'Loading…'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:flex-1">
              <Kpi label="Sent" value={latest?.sent} tone="paper-100"
                help={latest ? `${fmt(latest.total_leads)} leads · ${fmt(latest.still_active)} active` : '—'} />
              <Kpi label="Opens" value={latest?.unique_opens} tone="accent-400"
                help={latest ? `${fmt(latest.unique_clicks)} clicks` : '—'} />
              <Kpi label="Engaged replies" value={latest?.replies_engaged} tone="ok"
                help={latest ? `${fmt(latest.unique_replies)} total replies` : '—'} />
              <Kpi label="Bookings" value={latest?.bookings_count} tone="watch"
                help={latest ? `${fmt(latest.diag_questionnaire_filled)} diag filled` : '—'} />
            </div>
          </div>

          {/* @subsection 3.5 Error / unconfigured states */}
          {err && (
            <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 mb-6 text-stall text-sm">
              {err}
            </div>
          )}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 mb-6 text-sm text-watch">
              <div className="font-semibold mb-1">Campaigns pipeline isn't configured</div>
              <div className="text-muted">Backend returned 503. Awaiting first refresh.</div>
            </div>
          )}

          {/* @subsection 3.6 Body grid — chart + reply/sender/diag panels */}
          <div className="grid gap-6 grid-cols-1 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">

            {/* LEFT — timeseries (ported SVG TrendChart, daily activity) */}
            <div>
              {rows.length >= 2 ? (
                <TrendChart data={rows} metrics={METRICS} baseFields={BASE_FIELDS} title="Daily activity" />
              ) : (
                <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4 text-muted text-xs py-12 text-center">
                  Not enough daily data for a trend yet.
                </div>
              )}
            </div>

            {/* RIGHT — reply breakdown, sender health, diag funnel */}
            <div className="space-y-4">
              <Panel title="✉️ Reply classification">
                {latest ? (
                  <ul className="space-y-1.5 text-xs">
                    <Row label="Engaged" value={latest.replies_engaged} tone="text-ok" />
                    <Row label="Not interested" value={latest.replies_not_interested} tone="text-stall" />
                    <Row label="Redirect" value={latest.replies_redirect} tone="text-muted" />
                    <Row label="Auto · OOO" value={latest.replies_auto_ooo} tone="text-muted" />
                    <Row label="Auto · other" value={latest.replies_auto_other} tone="text-muted" />
                    <Row label="Unclassified" value={latest.replies_unclassified} tone="text-muted" />
                  </ul>
                ) : <Loading />}
              </Panel>

              <Panel title="📡 Sender health">
                {latest ? (
                  <ul className="space-y-1.5 text-xs">
                    <Row label="Senders used" value={latest.unique_senders_used} tone="text-paper-100" />
                    <Row label="With recent issues" value={latest.senders_with_recent_issues}
                         tone={latest.senders_with_recent_issues > 0 ? 'text-watch' : 'text-muted'} />
                  </ul>
                ) : <Loading />}
              </Panel>

              <Panel title="🧪 Diag funnel">
                {latest ? (
                  <ul className="space-y-1.5 text-xs">
                    <Row label="Tokens invited" value={latest.diag_tokens_invited} tone="text-muted" />
                    <Row label="Visited" value={latest.diag_tokens_visited} tone="text-paper-100" />
                    <Row label="Real browser" value={latest.diag_tokens_real_browser} tone="text-paper-100" />
                    <Row label="Filled" value={latest.diag_questionnaire_filled} tone="text-ok" />
                    <Row label="First-clicked" value={latest.diag_first_clicked_count} tone="text-accent-400" />
                  </ul>
                ) : <Loading />}
              </Panel>
            </div>

          </div>

        </div>
      </section>
    </div>
  );
}

// @section 4 Presentational helpers

// @block 4.1 KPI card (matches Overview shape)
function Kpi({ label, value, tone, help }: { label: string; value?: number; tone: string; help: string }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">{label}</div>
      <div className={`text-3xl md:text-4xl font-bold tabular-nums mt-1 text-${tone}`}>
        {value == null ? '—' : fmt(value)}
      </div>
      <div className="text-[11px] text-muted mt-0.5">{help}</div>
    </div>
  );
}

// @block 4.2 Panel card
function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

// @block 4.3 Label/value row
function Row({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-muted">{label}</span>
      <span className={`tabular-nums font-semibold ${tone}`}>{fmt(value)}</span>
    </li>
  );
}

function Loading() {
  return <div className="text-muted text-xs py-3 text-center">Loading…</div>;
}

// @section 5 Formatting
function fmt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}
function shortTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
